import {
    BY_TYPE_HIGHLIGHT_PREDICATES,
    BY_TYPE_LAYER_IDS,
    BY_TYPE_LAYER_PROPERTY,
    LISTING_STATUS_LAYER_IDS,
    LISTING_STATUS_LAYER_PROPERTY,
} from '../../enums/FacilityPinConfig';

export const UNCLUSTERED_POINTS_MIN_ZOOM = 8;

class GeoJsonLayersData {
    constructor(mapInstance, mapConfig, layers, sources) {
        this._mapConfig = mapConfig;
        this._mapInstance = mapInstance;
        this._dragonflyLayers = layers;
        this._dragonflySources = sources;

        this._geoJsonDragonflyDataSource = {};
        this._geoJsonDragonflyDataSourceId = undefined;
        /** @type {import('mapbox-gl').Layer[]} */
        this._geoJsonDragonflyLayers = [];
        // Original `icon-image` per layer (the default "by pricing" scheme that
        // ships in the points metadata) so the "by listing status" override is
        // reversible.
        this._originalIconImages = {};
    }

    get sourceId() {
        return this._geoJsonDragonflyDataSourceId;
    }

    get pointsLayerId() {
        return this._pointDataUnClusteredLayerId;
    }

    get source() {
        return this._geoJsonDragonflyDataSource;
    }

    get geoJsonSourceData() {
        return this._geoJsonSourceData;
    }

    get layers() {
        return this._geoJsonDragonflyLayers;
    }

    get idProperty() {
        return this._idProperty;
    }

    get pointsLayer() {
        return this._geoJsonDragonflyLayers.find(l => l.id === this._pointDataUnClusteredLayerId);
    }

    get clusterLayer() {
        return this._geoJsonDragonflyLayers.find(l => l.id === this._pointDataClusteredLayerId);
    }

    /**
     * @param {import('../../').PointsMetadata} geoJsonMetadata
     */
    setGeoJsonLayersData(geoJsonMetadata) {
        const { mapProperties, pointPreviewProperties } = geoJsonMetadata;
        this._geoJsonDragonflyDataSource = mapProperties.source;
        this._geoJsonDragonflyDataSourceId = mapProperties.sourceId;
        this._geoJsonDragonflyLayers = [...mapProperties.layers];
        this._pointDataClusteredLayerId = mapProperties.clusteredLayerId;
        this._pointDataUnClusteredLayerId = mapProperties.layerId;
        this._markerColors = mapProperties.markerColors;
        // id of the property that is unique for each feature
        this._idProperty = pointPreviewProperties.idProperty;

        // Capture the default icon-image expression for each symbol layer so the
        // facility pin config can be toggled and restored at runtime.
        this._originalIconImages = {};
        this._geoJsonDragonflyLayers.forEach(layer => {
            if (layer.layout && layer.layout['icon-image'] !== undefined) {
                this._originalIconImages[layer.id] = layer.layout['icon-image'];
            }
            // The hosted metadata gates the individual facility pins at minzoom 9,
            // which - with clustering off by default - hid facilities across regional
            // and metro views. Lower the gate to 8 so singular pins appear as soon as
            // you zoom into a market/region. Clustering lifts the gate entirely
            // (PointsHandler.applyGeoJSONClusteringToggle).
            if (layer.id === this._pointDataUnClusteredLayerId) {
                layer.minzoom = UNCLUSTERED_POINTS_MIN_ZOOM;
            }
            // Add ~1px more breathing room between each facility pin and its
            // label. text-offset is in ems; the metadata ships a static [x, y],
            // so nudge y downward by ~0.1em (~1px at the label's font size).
            const textOffset = layer.layout && layer.layout['text-offset'];
            if (
                Array.isArray(textOffset) &&
                textOffset.length === 2 &&
                typeof textOffset[1] === 'number'
            ) {
                layer.layout['text-offset'] = [textOffset[0], textOffset[1] + 0.1];
            }
        });

        // this._dragonflySources[this._geoJsonDragonflyDataSourceId] = this._geoJsonDragonflyDataSource;
        // this._geoJsonDragonflyLayers.forEach(gl =>
        //     this._dragonflyLayers.push(gl)
        // );
    }

    /**
     * Overrides the facility pin `icon-image` on the unclustered points layer
     * only — the single layer the click hit-test queries. Other symbol layers
     * (e.g. the label layer's small `place_labels` icon) keep their default
     * icon, otherwise they would draw duplicate, non-clickable pins on top.
     * Pass an expression for the "by listing status" config, or null/undefined
     * to restore the original "by pricing" scheme.
     * @param {*} iconImage Mapbox icon-image expression, or null to restore.
     */
    setFacilityPinIconImage(iconImage) {
        this._geoJsonDragonflyLayers.forEach(layer => {
            if (layer.id !== this._pointDataUnClusteredLayerId) return;
            if (this._originalIconImages[layer.id] === undefined) return;
            if (!layer.layout) layer.layout = {};
            layer.layout['icon-image'] = iconImage || this._originalIconImages[layer.id];
        });
    }

    /**
     * Flags each facility feature with `has_listing` based on the set of listed
     * facility ids fetched from the listings endpoint, so the listing-status
     * icon-image expression can read it. Also derives the listing-status
     * virtual-layer id (e.g. 'operating-listed') used by the legend's
     * show/hide filtering. Mutates the in-memory source data.
     * @param {Set<string>} listedIdSet
     * @param {string} [idProperty='facility_id']
     */
    mergeFeatureListingFlags(listedIdSet, idProperty = 'facility_id') {
        if (!this._geoJsonSourceData || !this._geoJsonSourceData.features) return;
        this._geoJsonSourceData.features.forEach(feature => {
            const props = feature.properties || {};
            const id = props[idProperty];
            props.has_listing =
                id !== undefined && id !== null && listedIdSet.has(String(id));

            // Normalize on_market once here (the backend may ship it as the
            // boolean `true` or the Postgres string `'t'`), so both the derived
            // virtual-layer property below and the icon-image expression — which
            // reads `['boolean', ['get','on_market'], false]` — agree, and match
            // how InfoBubblePointFeature interprets the same flag.
            props.on_market = props.on_market === true || props.on_market === 't';

            const group =
                props.category === 'construction'
                    ? LISTING_STATUS_LAYER_IDS.development
                    : LISTING_STATUS_LAYER_IDS.operating;
            props[LISTING_STATUS_LAYER_PROPERTY] = props.has_listing
                ? group.listed
                : props.on_market
                  ? group.onMarket
                  : group.offMarket;
            feature.properties = props;
        });
    }

    /**
     * Derives the by-type virtual-layer id (e.g. 'operating-highlighted') on every
     * feature so the by-type legend can hide/unhide highlighted and non-highlighted
     * pins independently. Must run AFTER mergeFeatureListingFlags — the predicates read
     * the `has_listing` / normalized `on_market` flags it writes.
     *
     * A null metric ("Highlight by: None") classifies everything as `plain`, which is
     * what makes the two-row default state fall out of the same code path.
     *
     * Mutates the in-memory source data, so it has to be re-run whenever the metric
     * changes or fresh features load (see PointsHandler).
     * @param {string|null} highlightMetric one of BY_TYPE_HIGHLIGHT_METRICS, or null
     */
    mergeByTypeLayerIds(highlightMetric) {
        if (!this._geoJsonSourceData || !this._geoJsonSourceData.features) return;
        const isHighlighted = BY_TYPE_HIGHLIGHT_PREDICATES[highlightMetric] || (() => false);
        this._geoJsonSourceData.features.forEach(feature => {
            const props = feature.properties || {};
            const group =
                props.category === 'construction'
                    ? BY_TYPE_LAYER_IDS.development
                    : BY_TYPE_LAYER_IDS.operating;
            props[BY_TYPE_LAYER_PROPERTY] = isHighlighted(props) ? group.highlighted : group.plain;
            feature.properties = props;
        });
    }

    resetClusterLayerColor() {
        if (!this.clusterLayer) return;
        this.clusterLayer.paint['circle-color'] = this._markerColors.default;
    }

    updateClusterLayerColor(visibleLayerId) {
        if (!this.clusterLayer) return;
        // Listing-status virtual layers have no entry in the metadata's
        // markerColors — keep the default cluster color for those.
        this.clusterLayer.paint['circle-color'] =
            this._markerColors[visibleLayerId] || this._markerColors.default;
    }

    /**
     * @param geoJson {import('../../').GeoJson}
     */
    setGeoJsonSourceData(geoJson) {
        this._geoJsonSourceData = JSON.parse(JSON.stringify(geoJson));
        this._geoJsonDragonflyDataSource.data = this._geoJsonSourceData;
    }

    setFilteredSourceData(filteredSourceData) {
        this._geoJsonDragonflyDataSource.data = filteredSourceData;
    }

    resetData() {
        this._geoJsonDragonflyDataSource.data = this._geoJsonSourceData;
    }

    update() {
        delete this._dragonflySources[this._geoJSONDragonflyDataSource.id];
        this._geoJSONDragonflyDataSource = undefined;
        this._geoJsonDragonflyLayers = [];
    }

}

export default GeoJsonLayersData;
